'use client';

import { useCallback, useEffect, useState } from 'react';
import Home2424 from '@svg/main/sideMenu/home_24_24.svg';
import Close2424 from '@svg/main/sideMenu/close_24_24.svg';
import SpinnerLoader1818 from '@svg/common/spinner_loader_18_18.svg';
import LoginArrowRight2020 from '@svg/main/sideMenu/login_arrow_right_20_20.svg';
import { signinWithZzimToken, signout } from '@/utils/actions';
import { UserCGType } from '@/types/graphql/root';
import usePlatformInfo from '@/app/hooks/usePlatformInfo';
import { sideMenuModalAtom } from '@/atom/modal';
import { useAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import usePreventBodyScroll from '@/app/hooks/usePreventBodyScroll';

const ZZIM_CAR_SERVICES_LIST = [
  { name: '여행플래너', url: process.env.NEXT_PUBLIC_PLANNER_URL },
  { name: '국내항공', url: process.env.NEXT_PUBLIC_ZZIMCAR_AIR_URL },
  {
    name: '해외항공',
    mobileUrl: process.env.NEXT_PUBLIC_ZZIMCAR_INT_AIR_MOBILE_URL,
    pcUrl: process.env.NEXT_PUBLIC_ZZIMCAR_INT_AIR_PC_URL,
  },
  {
    name: '국내숙소',
    url: process.env.NEXT_PUBLIC_ZZIMCAR_DOMESTIC_STAY_URL,
  },
  {
    name: '국내렌트카',
    mobileUrl: process.env.NEXT_PUBLIC_ZZIMCAR_RENTCAR_MOBILE_URL,
    pcUrl: process.env.NEXT_PUBLIC_ZZIMCAR_RENTCAR_PC_URL,
  },
  {
    name: '해외렌트카',
    url: process.env.NEXT_PUBLIC_ZZIMCAR_GLOBAL_RENTCAR_URL,
  },
  {
    name: '위클리렌트카',
    mobileUrl: process.env.NEXT_PUBLIC_ZZIMCAR_WEEKLY_RENTCAR_MOBILE_URL,
    pcUrl: process.env.NEXT_PUBLIC_ZZIMCAR_WEEKLY_RENTCAR_PC_URL,
  },
  {
    name: '투어택시',
    url: process.env.NEXT_PUBLIC_ZZIMCAR_TOUR_TAXI_URL,
  },
  { name: '전세버스', url: process.env.NEXT_PUBLIC_ZZIMCAR_RENTBUS_URL },
  {
    name: '기차여행',
    mobileUrl: process.env.NEXT_PUBLIC_ZZIMCAR_TRAIN_MOBILE_URL,
    pcUrl: process.env.NEXT_PUBLIC_ZZIMCAR_TRAIN_PC_URL,
  },
  {
    name: '카셰어링',
    url: process.env.NEXT_PUBLIC_ZZIMCAR_CAR_SHARING_URL,
  },
];

export default function SideMenu() {
  const { isMobile } = usePlatformInfo();

  const router = useRouter();

  const [userFetchFinished, setUserFetchFinished] = useState(false);
  const [user, setUser] = useState<UserCGType | null>(null);
  const [sideMenuModal, setSideMenuModal] = useAtom(sideMenuModalAtom);

  usePreventBodyScroll(sideMenuModal);

  useEffect(() => {
    if (!sideMenuModal) return;
    setUserFetchFinished(false);
    signinWithZzimToken()
      .then((signinResult) => {
        setUser(signinResult?.user ?? null);
      })
      .finally(() => {
        setUserFetchFinished(true);
      });
  }, [sideMenuModal]);

  const signoutHandler = useCallback(async () => {
    await signout();
    setUser(null);
  }, []);

  if (!sideMenuModal) return null;

  return (
    <div className='fixed w-full max-w-480pxr top-0 z-20 h-full overflow-x-hidden'>
      <div className={`side-menu absolute w-full max-w-480pxr top-0 h-full bg-white overflow-y-scroll`}>
        {/* side menu top bar */}
        <div className='w-full px-16pxr h-56pxr items-center justify-between flex'>
          <button
            onClick={() => {
              router.back();
              window.location.href = process.env.NEXT_PUBLIC_ZZIMCAR_URL ?? '';
            }}
          >
            <Home2424 />
          </button>
          <button onClick={() => router.back()}>
            <Close2424 />
          </button>
        </div>

        {/* side menu login box */}
        <div className='w-full px-30pxr py-20pxr h-94pxr'>
          <button
            className='w-full h-54pxr p-2pxr rounded-100pxr'
            style={{
              background: 'linear-gradient(271deg, #33A1FC 0.32%, #2FE8BC 99.61%, #2FE8BC 99.61%)',
            }}
            onClick={() => {
              if (!userFetchFinished) return;
              if (!user) {
                router.back();
                window.location.href =
                  process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'user/signin?redirectUrl=' + window.location.origin;
              } else {
                router.back();
                window.location.href = process.env.NEXT_PUBLIC_ZZIMCAR_URL + 'mypage';
              }
            }}
          >
            <div
              className={`flex flex-row items-center justify-between w-full h-full bg-white rounded-100pxr pl-20pxr pr-10pxr`}
            >
              {!userFetchFinished && <SpinnerLoader1818 className='animate-spin' />}
              {userFetchFinished && user && (
                <p className='text-gray-700 body-16-md'>
                  <span className='text-custom-black'>{user.name}</span>님의 마이페이지
                </p>
              )}
              {userFetchFinished && !user && <p className='text-gray-700 body-16-md'> 로그인을 해주세요</p>}
              <LoginArrowRight2020 />
            </div>
          </button>
        </div>

        <div className='flex flex-row items-center px-30pxr h-54pxr'>
          <p className='text-custom-black text-18pxr leading-[27px] -tracking-[0.2px] font-medium'>찜카 서비스</p>
        </div>
        <ul className='text-[#6F7070] sub-heading-16-m pb-16pxr'>
          {ZZIM_CAR_SERVICES_LIST.map((service, index) => {
            const targetUrl = service.url ?? (isMobile ? service.mobileUrl : service.pcUrl);
            return (
              <li
                key={'zzimcarService_' + index}
                className='flex items-center cursor-pointer pl-40pxr py-10pxr hover:font-bold h-48pxr'
                onClick={() => {
                  router.back();
                  if (targetUrl) window.location.href = targetUrl;
                }}
              >
                {service.name}
              </li>
            );
          })}
        </ul>

        <button
          className='flex flex-row items-center w-full px-30pxr h-54pxr'
          onClick={() => {
            router.back();
            window.location.href = `${process.env.NEXT_PUBLIC_ZZIMCAR_URL}notice-event`;
          }}
        >
          <p className='text-custom-black text-18pxr leading-[27px] -tracking-[0.2px] font-medium'>공지사항/이벤트</p>
        </button>

        <button
          className='flex flex-row items-center w-full px-30pxr h-54pxr'
          onClick={() => {
            router.back();
            window.location.href = `${process.env.NEXT_PUBLIC_ZZIMCAR_URL}support`;
          }}
        >
          <p className='text-custom-black text-18pxr leading-[27px] -tracking-[0.2px] font-medium'>고객지원</p>
        </button>

        {user && (
          <footer className='py-24pxr px-30pxr'>
            <button
              className='w-full py-12pxr items-center justify-center flex rounded-10pxr bg-[#ECEEF0]'
              onClick={() => {
                signoutHandler();
              }}
            >
              <p className='text-[#0A0A0A] text-16pxr leading-[1.5rem] font-medium'>로그아웃</p>
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
